import { getOnLoadRegBranchPayloadFromStorage } from '../../utils/regBranchOnLoadPayload';
import { getManagingBranchNameForApi } from '../../utils/managingBranchName';
import { ALL_ROUTES, BLANK_ROUTE_ENTRY } from './branchTeamsConfig';

export function formatTeamsErrors(errors) {
  if (!Array.isArray(errors)) return null;
  const parts = errors
    .map((e) => (typeof e?.ErrorMessage === 'string' ? e.ErrorMessage.trim() : ''))
    .filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

export function formatCell(value) {
  if (value == null || value === '') return '—';
  return String(value);
}

export function formatDate(value) {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? String(value)
    : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function getInitials(firstName, lastName) {
  const f = (firstName || '').trim()[0] || '';
  const l = (lastName || '').trim()[0] || '';
  return (f + l).toUpperCase() || '?';
}

/** Single grey for all member initial avatars (list, add, edit). */
export const AVATAR_GREY_BG = '#94A3B8';

export function avatarColor(_name) {
  return AVATAR_GREY_BG;
}

/** Shared MUI Avatar sx for initials boxes. */
export function avatarInitialSx(overrides = {}) {
  return {
    bgcolor: AVATAR_GREY_BG,
    color: '#FFFFFF',
    fontFamily: 'Inter Bold',
    boxShadow: '0 2px 8px rgba(100, 116, 139, 0.28)',
    border: '2px solid #fff',
    outline: '1px solid rgba(148, 163, 184, 0.35)',
    ...overrides,
  };
}

export function resolveBranchNameForQuery() {
  const fromSession = getOnLoadRegBranchPayloadFromStorage()?.branchName;
  if (fromSession) return fromSession;
  return getManagingBranchNameForApi();
}

/** New API: each item is `{ TeamInfo, TeamRoleAccess }`; legacy: flat team object. */
export function isNestedTeamRow(row) {
  return row != null && typeof row.TeamInfo === 'object' && row.TeamInfo !== null;
}

export function getTeamInfo(row) {
  if (isNestedTeamRow(row)) return row.TeamInfo;
  return row;
}

export function getTeamRoleAccess(row) {
  if (isNestedTeamRow(row)) return row.TeamRoleAccess || {};
  return {};
}

export function getTeamRowKey(row, idx) {
  const info = getTeamInfo(row);
  return String(info._id || info.team_user_id || info.email || idx);
}

export function isAccountActive(info) {
  const a = info?.account_status;
  if (a === true) return true;
  return String(a || '').toLowerCase() === 'active';
}

export function isAccountPending(info) {
  if (isAccountActive(info)) return false;
  const rs = String(info?.reason_status || '').toLowerCase();
  if (rs.includes('pending')) return true;
  return String(info?.account_status || '').toLowerCase() === 'pending';
}

/** True when API sends boolean true or common verified string values. */
export function isVerifiedStatus(value) {
  if (value === true) return true;
  return ['true', 'yes', 'verified', '1'].includes(String(value ?? '').toLowerCase());
}

export function routeAccessLabel(routePath) {
  const norm = String(routePath || '').trim().toLowerCase();
  const found = ALL_ROUTES.find((r) => r.route === norm);
  return found ? found.label : formatCell(routePath);
}

export function buildBlankRouteAccess() {
  return ALL_ROUTES.map((r) => BLANK_ROUTE_ENTRY(r.route));
}

/** Merge GET roster `routeAccess` into the full dashboard route list for editing. */
export function mergeMemberRouteAccess(member) {
  const raw = getTeamRoleAccess(member);
  const fromServer = Array.isArray(raw?.routeAccess)
    ? raw.routeAccess
    : Array.isArray(member?.routeAccess)
      ? member.routeAccess
      : [];
  return ALL_ROUTES.map((ar) => {
    const found = fromServer.find(
      (s) => String(s?.route ?? '').trim().toLowerCase() === ar.route
    );
    if (!found) return BLANK_ROUTE_ENTRY(ar.route);
    const acc = found.access && typeof found.access === 'object' ? found.access : {};
    const rowOn =
      found.is_active === undefined || found.is_active === null
        ? true
        : !!(found.is_active === true || found.is_active === 'true' || found.is_active === 1);
    return {
      route: ar.route,
      is_active: rowOn,
      access: {
        read: !!acc.read,
        write: !!acc.write,
        edit: !!acc.edit,
        delete: !!acc.delete,
      },
    };
  });
}

export function parseCommaSeparatedList(str) {
  return String(str ?? '')
    .split(/[,;\n]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

export function formatApiMessage(body) {
  if (!body) return null;
  return (
    body.customMessage ||
    body.CustomMessage ||
    body.message ||
    null
  );
}
